import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import CursorDefaultOutlineIcon from 'mdi-react/CursorDefaultOutlineIcon';
import VectorPolylineEditIcon from 'mdi-react/VectorPolylineEditIcon';
import DeskIcon from 'mdi-react/DeskIcon';
import TableChairIcon from 'mdi-react/TableChairIcon';
import TableFurnitureIcon from 'mdi-react/TableFurnitureIcon';
import FormatTextIcon from 'mdi-react/FormatTextIcon';
import SignDirectionIcon from 'mdi-react/SignDirectionIcon';
import VectorPolygonIcon from 'mdi-react/VectorPolygonIcon';
import RectangleOutlineIcon from 'mdi-react/RectangleOutlineIcon';
import EllipseOutlineIcon from 'mdi-react/EllipseOutlineIcon';
import GridIcon from 'mdi-react/GridIcon';
import { needsScale, type EditorTool } from '../models/editor-tool';
import { editorTheme, floatingSlabSx } from '../utils/editor-theme';

interface Props {
  readonly tool: EditorTool;
  readonly onToolChange: (tool: EditorTool) => void;
  /**
   * The background ruling, switched from the rail rather than from a slab of its own.
   *
   * It sits with the tools because that is where the hand already is, and because a single
   * floating control in the corner of the drawing was a whole piece of furniture for one
   * switch.
   */
  readonly showGrid: boolean;
  readonly onShowGridChange: (show: boolean) => void;
  /**
   * Whether the plan has a measured scale.
   *
   * A desk group is laid out in metres, so without one there are no dimensions to lay it out
   * at. That tool is disabled and says why, rather than producing a block of desks a fifth of
   * a millimetre wide on a plan in millimetres and looking broken.
   */
  readonly hasScale?: boolean;
}

const TOOLS: ReadonlyArray<{
  readonly tool: EditorTool;
  readonly labelKey: string;
  readonly shortcut: string;
  readonly Icon: typeof CursorDefaultOutlineIcon;
}> = [
  // A desk marker is a bookable seat, a desk shape is the furniture drawn under it, and an
  // icon annotation is wayfinding signage — printers, fire extinguishers, toilets. The old
  // set said "shape with a plus" and "smiley face", neither of which is any of those.
  { tool: 'select', labelKey: 'floorplan-editor-tool-select', shortcut: 'V', Icon: CursorDefaultOutlineIcon },
  // Reshaping is a different job from moving, and it is the only way to extend or shrink an
  // outline: move, turn and resize all change where a shape is, never what shape it is.
  // A polyline with a pencil, not a lone point: the tool edits the *shape* of an outline, and
  // a single dot said "place a point", which is what every other tool in this group does.
  { tool: 'vertex', labelKey: 'floorplan-editor-tool-vertex', shortcut: 'E', Icon: VectorPolylineEditIcon },
  { tool: 'desk', labelKey: 'floorplan-editor-tool-desk', shortcut: 'D', Icon: DeskIcon },
  // A table with chairs round it: a group is seats, which is what makes it different from the
  // furniture tool below. It was a grid of squares, which now means the ruling switch.
  { tool: 'deskGroup', labelKey: 'floorplan-editor-tool-desk-group', shortcut: 'B', Icon: TableChairIcon },
  { tool: 'deskShape', labelKey: 'floorplan-editor-tool-desk-shape', shortcut: 'S', Icon: TableFurnitureIcon },
  { tool: 'text', labelKey: 'floorplan-editor-tool-text', shortcut: 'T', Icon: FormatTextIcon },
  { tool: 'icon', labelKey: 'floorplan-editor-tool-icon', shortcut: 'I', Icon: SignDirectionIcon },
];

/** Drawing real geometry, as opposed to placing a marker on top of it. */
const DRAW_TOOLS: typeof TOOLS = [
  { tool: 'polygon', labelKey: 'floorplan-editor-tool-polygon', shortcut: 'P', Icon: VectorPolygonIcon },
  { tool: 'rectangle', labelKey: 'floorplan-editor-tool-rectangle', shortcut: 'R', Icon: RectangleOutlineIcon },
  // An outlined ellipse rather than a circle: a square drag gives a circle, but the tool draws
  // an oval just as readily, and the icon should not promise less than the tool does.
  { tool: 'ellipse', labelKey: 'floorplan-editor-tool-ellipse', shortcut: 'C', Icon: EllipseOutlineIcon },
];

/**
 * Every tool, one active at a time, each with its keyboard shortcut.
 *
 * Styled as a floating slab over the drawing rather than as a bordered column beside it.
 * That is the convention every drawing tool uses, and it is not decoration: the rail is a
 * thing you reach *to* and then come back from, so it belongs over the work rather than
 * taking a column of width away from it permanently.
 */
const EditorToolRail = ({
  tool, onToolChange, showGrid, onShowGridChange, hasScale = false,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();

  const renderGroup = (group: typeof TOOLS): React.JSX.Element => (
    // No `onChange` on the group. It injects its handler into its *direct* children, and a
    // disabled button needs a `<span>` wrapper to keep its tooltip — which would swallow the
    // injection and leave those buttons doing nothing at all when clicked. Each button gets
    // its own handler instead, which does not care what it is wrapped in.
    <ToggleButtonGroup
      orientation="vertical"
      exclusive
      value={tool}
      aria-label={String(t('floorplan-editor-tools'))}
      size="small"
      sx={{
        // Borderless, so the group reads as a strip of icons rather than as a stack of
        // outlined boxes. The slab underneath is what holds them together.
        '& .MuiToggleButton-root': {
          border: 'none',
          borderRadius: `${editorTheme.radiusSmall}px !important`,
          color: editorTheme.inkDim,
          width: 36,
          height: 34,
          '&:hover': { backgroundColor: editorTheme.surfaceSunken, color: editorTheme.ink },
          '&.Mui-selected': {
            backgroundColor: editorTheme.accent,
            color: editorTheme.accentInk,
            '&:hover': { backgroundColor: editorTheme.accent },
          },
          '&.Mui-disabled': { border: 'none', color: editorTheme.inkFaint, opacity: 0.45 },
          // Stated, because MUI's default outline vanishes against the accent fill of the
          // active tool — and this rail is driven from the keyboard.
          '&:focus-visible': { boxShadow: editorTheme.focusRing, zIndex: 1 },
        },
      }}
    >
      {group.map(({ tool: value, labelKey, shortcut, Icon }) => {
        const disabled = needsScale(value) && !hasScale;
        const label = String(t(labelKey));

        return (
          <Tooltip
            key={value}
            title={disabled
              ? String(t('floorplan-editor-tool-needs-scale'))
              : `${label}${shortcut ? ` (${shortcut})` : ''}`}
            placement="right"
          >
            {/* Wrapped so a disabled button still has a tooltip: MUI drops pointer events on a
                disabled control, and "why can I not draw a wall?" is exactly the question. */}
            <span>
              {/* `selected` is set explicitly rather than left to the group. The group injects
                  it into its direct children, which here are the Tooltips, so it never reaches
                  the buttons — leaving every one of them aria-pressed="false" and the active
                  tool distinguishable only by colour. */}
              <ToggleButton
                value={value}
                selected={tool === value}
                disabled={disabled}
                aria-label={label}
                onClick={(): void => onToolChange(value)}
              >
                <Icon size={20} />
              </ToggleButton>
            </span>
          </Tooltip>
        );
      })}
    </ToggleButtonGroup>
  );

  return (
    <Box
      sx={{
        ...floatingSlabSx,
        // Placed by the editor, which owns where the rail sits on the stage.
        position: 'static',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.25,
        p: 0.5,
      }}
    >
      {renderGroup(TOOLS)}
      <Divider flexItem sx={{ my: 0.25, borderColor: editorTheme.lineSoft }} />
      {renderGroup(DRAW_TOOLS)}
      <Divider flexItem sx={{ my: 0.25, borderColor: editorTheme.lineSoft }} />

      {/* Not a tool: it changes nothing on the plan and stays on while other tools are used.
          Separated by the same rule the tool groups use, and pressed rather than selected. */}
      <Tooltip title={`${String(t('floorplan-editor-grid-lines'))} (G)`} placement="right">
        <ToggleButton
          value="grid"
          size="small"
          selected={showGrid}
          aria-label={String(t('floorplan-editor-grid-lines'))}
          onClick={(): void => onShowGridChange(!showGrid)}
          sx={{
            border: 'none',
            borderRadius: `${editorTheme.radiusSmall}px !important`,
            color: editorTheme.inkDim,
            width: 36,
            height: 34,
            '&:hover': { backgroundColor: editorTheme.surfaceSunken, color: editorTheme.ink },
            '&.Mui-selected': {
              backgroundColor: editorTheme.accentSoft,
              color: editorTheme.accent,
              '&:hover': { backgroundColor: editorTheme.accentSoft },
            },
            '&:focus-visible': { boxShadow: editorTheme.focusRing, zIndex: 1 },
          }}
        >
          <GridIcon size={20} />
        </ToggleButton>
      </Tooltip>
    </Box>
  );
};

export default EditorToolRail;
